import { useLocation } from "react-router";
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
} from "@/components/ui/sidebar";
import AccessGuard from "@/components/access-guard";
import { FeatureFlag } from "@/constants";
import { IlluminateUserPermissionsEnum } from "@/api/openapi";
import { useDeviceDetail } from "@/api/device-http-functions";
import { isCoreChargerLogsDevice } from "@/utils/isCoreChargerLogsDevice";
import { LoadManagementLinks } from "@/components/load-management-links";

/**
 * Matches the flattened Load Management routes exposed directly under a
 * site-less CORE device, e.g. `/devices/:deviceIdentity/core-configuration`
 * or `/manage-customers/devices/:deviceIdentity/site-diagram`.
 */
const LOAD_MANAGEMENT_DEVICE_PATTERN =
    /\/devices\/([^/]+)\/(?:core-configuration|core-charger-logs|site-diagram|asat)(?:\/|$)/;

/**
 * Renders the Load Management nav links while the user is on a CORE device's
 * flattened core-config sub-routes (the escape hatch for CORE devices without a
 * site). Hidden everywhere else. Used by sidebars that sit above the device
 * route (DevicesSidebar, ManageCustomersSidebar) and so can't rely on
 * `useParams` for the device identity.
 */
export const DeviceLoadManagementSidebarGroup = () => {
    const location = useLocation();

    const match = location.pathname.match(LOAD_MANAGEMENT_DEVICE_PATTERN);

    const deviceIdentity = match ? decodeURIComponent(match[1]) : undefined;

    const { data: device } = useDeviceDetail(deviceIdentity ?? "", undefined, {
        query: { enabled: !!deviceIdentity },
    });

    if (!match || !deviceIdentity) {
        return null;
    }

    const base = `${location.pathname.slice(0, match.index)}/devices/${match[1]}`;

    return (
        <AccessGuard
            unauthorizedBehaviour="hide"
            featureFlags={[FeatureFlag.IL_4187_LOAD_MANAGEMENT]}
            permissions={[
                IlluminateUserPermissionsEnum.SystemDeviceProvisioning,
                IlluminateUserPermissionsEnum.PartnerDeviceProvisioning,
            ]}
            permissionsSelector="any"
        >
            <SidebarGroup>
                <SidebarGroupLabel>Load management</SidebarGroupLabel>
                <SidebarGroupContent>
                    <SidebarMenu>
                        <LoadManagementLinks
                            base={base}
                            isCoreChargerLogs={isCoreChargerLogsDevice(device)}
                            nested={false}
                        />
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>
        </AccessGuard>
    );
};
